import { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Header from './partials/Header';
import AgencyIcon from './agency/AgencyIcon';
import ResultCard from './results/ResultCard';
import { useFavorites } from './FavoritesContext';
import { getRecentSearch, isAddressSearch, saveRecentSearch } from './utils/recentSearch';
import { toPublicAgencyCard } from './utils/agencyUrl';
import useAvailableAgencies from './utils/useAvailableAgencies';
import '../css/Results.css';
import '../css/ResultsPage.css';

export default function Results() {
    const { state } = useLocation();
    const [recentSearch] = useState(getRecentSearch);
    const search = isAddressSearch(state) ? state : recentSearch;
    const savedCards = useMemo(() => (search?.cards || []).map(toPublicAgencyCard).filter(Boolean), [search]);
    const incompatibleResults = search?.cards?.length > 0 && savedCards.length === 0;
    const { cards, loading, error, retry, unavailableIds } = useAvailableAgencies(savedCards);
    const searchLocation = search?.searchLocation || '';
    const [feedback, setFeedback] = useState('');
    const { favorites, isFavorite, toggleFavorite, updateFavoriteDetails, removeFavorites } = useFavorites();

    useEffect(() => {
        if (!search || incompatibleResults || loading || error) return;
        saveRecentSearch({ ...search, cards });
        updateFavoriteDetails(cards);
        if (unavailableIds.length) removeFavorites(unavailableIds);
    }, [search, incompatibleResults, cards, loading, error, unavailableIds, updateFavoriteDetails, removeFavorites]);

    const saveFavorite = card => {
        const wasFavorite = isFavorite(card.publicId);
        toggleFavorite({ ...card, searchLocation });
        setFeedback(`${card.name || 'Agenzia'} ${wasFavorite ? 'rimossa dai' : 'aggiunta ai'} preferiti.`);
    };

    return (
        <div className="ef-page ef-results-page">
            <Header />
            <main>
                <section className="ef-results-hero" aria-labelledby="results-title">
                    <div className="ef-results-container">
                        <p className="ef-results-kicker">La tua ricerca, una scelta consapevole</p>
                        <h1 id="results-title">Le agenzie vicine a te.</h1>
                        <p className="ef-results-hero__description">Trova le onoranze funebri vicino all’indirizzo indicato e contatta direttamente l’agenzia più adatta alle tue esigenze.</p>
                        <p className="ef-results-note">Sono mostrate le agenzie aderenti con servizio di visibilità attivo, ordinate per distanza dall’indirizzo indicato. <Link to="/informazioni-legali#come-funziona">Come funziona la ricerca</Link></p>
                        <div className="ef-results-search-summary">
                            <span className="ef-results-search-summary__icon"><AgencyIcon name="pin" /></span>
                            <div><span>Indirizzo della ricerca</span><strong>{searchLocation || (search ? 'L’indirizzo della tua ricerca' : 'Da dove vuoi iniziare?')}</strong></div>
                            <Link to="/#servizi-funebri" className="ef-results-button ef-results-button--secondary">{search ? 'Modifica ricerca' : 'Inizia la ricerca'} <span aria-hidden="true">↗</span></Link>
                        </div>
                    </div>
                </section>

                <div className="ef-results-container ef-results-layout">
                    <div className="ef-results-main">
                        <div className="ef-results-list-heading">
                            <div>
                                <h2>{search ? 'I tuoi risultati' : 'Trova un’agenzia nella tua zona'}</h2>
                                <p role="status" aria-live="polite" aria-atomic="true">{loading ? 'Verifica delle agenzie in corso…' : error ? 'Verifica delle agenzie non disponibile' : cards.length > 0 ? `${cards.length} ${cards.length === 1 ? 'agenzia trovata' : 'agenzie trovate'}` : incompatibleResults ? 'Schede temporaneamente non disponibili' : search ? 'Nessuna agenzia trovata' : 'Inizia indicando un indirizzo.'}</p>
                            </div>
                            {cards.length > 0 && <span className="ef-results-distance-order"><AgencyIcon name="pin" /> Dalle più vicine</span>}
                        </div>

                        {loading ? <div className="ef-results-empty" role="status"><p>Controlliamo che le agenzie siano ancora disponibili.</p></div> : error ? <div className="ef-results-empty" role="alert">
                            <h3>Non è stato possibile verificare le agenzie.</h3>
                            <p>Riprova per visualizzare i risultati aggiornati.</p>
                            <button type="button" className="ef-results-button bg-primary" onClick={retry}>Riprova</button>
                        </div> : cards.length > 0 ? <div className="ef-results-list" aria-label="Agenzie trovate">
                            {cards.map(card => <ResultCard key={card.publicId} data={card} searchLocation={searchLocation} isFavorite={isFavorite(card.publicId)} onFavorite={() => saveFavorite(card)} />)}
                        </div> : <div className="ef-results-empty">
                            <span className="ef-results-empty__icon"><AgencyIcon name="search" /></span>
                            <h3>{incompatibleResults ? 'Le schede non sono disponibili al momento.' : search ? 'Proviamo con un’altra ricerca.' : 'Il primo passo è scegliere un indirizzo.'}</h3>
                            <p>{incompatibleResults ? 'Non è stato possibile caricare le schede delle agenzie. Ripeti la ricerca tra qualche istante.' : search ? 'Non abbiamo trovato agenzie per questo indirizzo. Puoi cercare in un’altra zona.' : 'Indica dove hai bisogno del servizio per vedere le agenzie vicine.'}</p>
                            <Link to="/#servizi-funebri" className="ef-results-button bg-primary">{search ? 'Modifica la ricerca' : 'Cerca un’agenzia'} <span aria-hidden="true">→</span></Link>
                        </div>}
                    </div>

                    <aside className="ef-results-aside" aria-label="Consigli per la scelta">
                        <div className="ef-results-guide">
                            <span className="ef-results-guide__icon"><AgencyIcon name="phone" /></span>
                            <p className="ef-results-kicker">Scegli con calma</p><h2>Un contatto può fare chiarezza.</h2>
                            <p>Parlare con l’agenzia ti aiuta a capire cosa comprende il servizio.</p>
                            <ul><li><AgencyIcon name="check" /> Chiedi quali servizi sono inclusi.</li><li><AgencyIcon name="check" /> Verifica disponibilità e costi.</li><li><AgencyIcon name="check" /> Richiedi un preventivo dettagliato.</li></ul>
                            <span className="ef-results-guide__note">Chiamate ed email arrivano direttamente all’agenzia.</span>
                        </div>
                        <div className="ef-results-saved">
                            <AgencyIcon name="heart" /><h2>Ritrova le tue preferite.</h2>
                            <p>Salva le agenzie con il cuore e torna a consultarle quando vuoi.</p>
                            <Link to="/preferiti">Vedi i preferiti{favorites.length > 0 && <span className="ef-results-saved__count">{favorites.length}</span>} <span aria-hidden="true">→</span></Link>
                        </div>
                    </aside>
                </div>
                <p className="sr-only" role="status" aria-live="polite" aria-atomic="true">{feedback}</p>
            </main>
        </div>
    );
}
